/*
 * As regras PURAS do campo de país (#3669): a bandeira derivada do código, o
 * filtro sem acento e a lista de opções sobre o catálogo do voll-places. Sem
 * tabela de países e sem tabela de emojis — o código ISO alpha-2 é a única
 * fonte, e o `name` é sempre o do catálogo.
 */

/** Um país do catálogo. `name` é o que se GRAVA (perfil, payload da reserva);
 *  `label` é o que se MOSTRA quando o servidor traduziu para o idioma da
 *  sessão. Ausente ⇒ mostra o `name`. */
export interface CountryCatalogItem {
  readonly code: string;
  readonly name: string;
  readonly label?: string;
}

/** O par gravado: `code` ISO alpha-2 e o `name` do catálogo. */
export interface CountryValue {
  readonly code: string;
  readonly name: string;
}

export interface CountryOption {
  readonly code: string;
  readonly name: string;
  readonly label: string;
  readonly flag: string;
  /** O código gravado que o catálogo NÃO conhece: entra como opção extra com o
   *  `name` já gravado, para a pessoa reconhecer o próprio país (#3651). */
  readonly outsideCatalog: boolean;
}

/** `br` → `BR`; o `Select` compara por igualdade estrita e um código em
 *  minúsculas gravado pelo legado não casaria com o do catálogo. */
export function normalizeCountryCode(code: string | undefined): string {
  return code?.trim().toUpperCase() ?? '';
}

/** A bandeira de um alpha-2 pelos "regional indicator symbols"; fora do
 *  formato, nada — um código torto não ganha um glifo inventado. */
export function flagOf(code: string): string {
  const normalized = normalizeCountryCode(code);
  if (!/^[A-Z]{2}$/.test(normalized)) return '';
  const indicator = (index: number) => 0x1f1e6 + (normalized.codePointAt(index) ?? 65) - 65;
  return String.fromCodePoint(indicator(0), indicator(1));
}

/** Sem acento e em minúsculas — a chave de comparação dos dois lados:
 *  "alema" acha "Alemanha", "reuniao" acha "Reunião". */
function fold(text: string): string {
  return text.normalize('NFD').replaceAll(/\p{M}/gu, '').toLowerCase();
}

function toOption(item: CountryCatalogItem, outsideCatalog: boolean): CountryOption {
  const code = normalizeCountryCode(item.code);
  return {
    code,
    name: item.name,
    label: item.label?.trim() || item.name,
    flag: flagOf(code),
    outsideCatalog,
  };
}

/**
 * As opções do campo: o catálogo na ordem em que veio, precedido — quando o
 * código gravado não está nele — pela opção extra com o `name` do perfil. Sem
 * `name` gravado a opção extra mostra o próprio código: melhor um "AS" honesto
 * que sumir com a nacionalidade da pessoa.
 */
export function countryOptions(
  countries: readonly CountryCatalogItem[],
  value: CountryValue | undefined,
): CountryOption[] {
  const options = countries.map((item) => toOption(item, false));
  const saved = normalizeCountryCode(value?.code);
  if (saved === '' || options.some((option) => option.code === saved)) return options;
  const name = value?.name.trim() || saved;
  return [toOption({ code: saved, name }, true), ...options];
}

/** Filtro local: rótulo, nome gravado ou código (o código só por igualdade —
 *  "a" não pode devolver todo país cujo código tem um A). */
export function filterCountryOptions(
  options: readonly CountryOption[],
  term: string,
): CountryOption[] {
  const needle = fold(term.trim());
  if (needle === '') return [...options];
  return options.filter(
    (option) =>
      fold(option.label).includes(needle) ||
      fold(option.name).includes(needle) ||
      option.code.toLowerCase() === needle,
  );
}
